import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "react-toastify";

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../ui/table";
import IntentLeadService from "../../services/IntentLeadService";
import { cn } from "../../utils/styles.utils";

const INTENT_LEAD_COLUMNS = [
  { key: "serialNumber", label: "S.NO", minWidth: 90 },
  { key: "name", label: "Name", minWidth: 220 },
  { key: "companyName", label: "Company", minWidth: 220 },
  { key: "domain", label: "Domain", minWidth: 220 },
  { key: "industry", label: "Industry", minWidth: 180 },
  { key: "location", label: "Location", minWidth: 180 },
  { key: "intentScore", label: "Intent Score", minWidth: 150 },
  { key: "email", label: "Email", minWidth: 250 },
  { key: "designation", label: "Designation", minWidth: 200 },
  { key: "aiSummary", label: "AI Summary", minWidth: 240 },
  { key: "createdOn", label: "Created On", minWidth: 170 },
  { key: "action", label: "Action", minWidth: 140 },
];

const TOP_INTENT_LEAD_COUNT = 5;
const MAX_VISIBLE_ROWS = 5;
const COLUMN_SCROLL_THRESHOLD = 5;
const TABLE_HEADER_HEIGHT = 56;
const TABLE_ROW_HEIGHT = 56;
const AI_SUMMARY_COLUMN_WIDTH = 360;
const AI_SUMMARY_TEXT_WIDTH = 340;

const IntentLeadsBoard = () => {
  const [intentLeads, setIntentLeads] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  const [addingLeadId, setAddingLeadId] = useState(null);
  const [addedLeadIds, setAddedLeadIds] = useState(() => new Set());
  const [leadPendingConfirmation, setLeadPendingConfirmation] = useState(null);

  const loadIntentLeads = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");
      const response = await IntentLeadService.getTopIntentLeads(
        TOP_INTENT_LEAD_COUNT,
      );
      setIntentLeads(response.data.slice(0, TOP_INTENT_LEAD_COUNT));
    } catch (err) {
      console.error("Error fetching top intent leads:", err);
      setError("Failed to load intent leads.");
      setIntentLeads([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadIntentLeads();
  }, [loadIntentLeads]);

  const handleAddLeadClick = useCallback((intentLead) => {
    setLeadPendingConfirmation(intentLead);
  }, []);

  const handleCloseConfirmation = useCallback(() => {
    if (!addingLeadId) {
      setLeadPendingConfirmation(null);
    }
  }, [addingLeadId]);

  const handleConfirmAddLead = useCallback(async () => {
    const intentLead = leadPendingConfirmation;
    if (!intentLead) return;

    const rowKey = intentLead.rowKey;
    const intentLeadId = intentLead.intentLeadId ?? rowKey;

    if (!intentLeadId) {
      toast.error("Unable to add this intent lead. Missing intent lead id.");
      setLeadPendingConfirmation(null);
      return;
    }

    try {
      setAddingLeadId(rowKey);
      const response = await IntentLeadService.convertIntentLead(intentLeadId);

      if (response?.data?.success && response?.data?.data !== false) {
        setAddedLeadIds((previousIds) => {
          const nextIds = new Set(previousIds);
          nextIds.add(rowKey);
          return nextIds;
        });
        toast.success("Lead added successfully.");
        setLeadPendingConfirmation(null);
        await loadIntentLeads();
      } else {
        toast.error(response?.data?.message || "Failed to add lead.");
      }
    } catch (err) {
      console.error("Error converting intent lead:", err);
      toast.error(
        err?.response?.data?.message || "Failed to add lead. Please try again.",
      );
    } finally {
      setAddingLeadId(null);
    }
  }, [leadPendingConfirmation, loadIntentLeads]);

  const actionHandlers = useMemo(
    () =>
      intentLeads.reduce((handlers, intentLead) => {
        handlers[intentLead.rowKey] = () => handleAddLeadClick(intentLead);
        return handlers;
      }, {}),
    [handleAddLeadClick, intentLeads],
  );

  const shouldEnableHorizontalScroll =
    INTENT_LEAD_COLUMNS.length > COLUMN_SCROLL_THRESHOLD;
  const tableViewportHeight =
    TABLE_HEADER_HEIGHT + MAX_VISIBLE_ROWS * TABLE_ROW_HEIGHT;
  const shouldShowEmptyState = !isLoading && !error && intentLeads.length === 0;
  const isConfirmingLead = Boolean(
    leadPendingConfirmation && addingLeadId === leadPendingConfirmation.rowKey,
  );

  return (
    <div className="mb-4 min-w-0">
      <h6 className="fw-bold mb-3">Intent Leads</h6>
      <div
        className="w-full min-w-0 max-w-full rounded-2xl"
        style={{
          overflowX: shouldEnableHorizontalScroll ? "auto" : "hidden",
          overflowY: "auto",
          maxHeight: `${tableViewportHeight}px`,
          maxWidth: "100%",
          overscrollBehavior: "contain",
        }}
      >
        <Table
          className="w-max min-w-full"
          searchable={false}
          stickyHeader
          stickyHeaderOffset={0}
        >
          <TableHeader>
            <TableRow>
              {INTENT_LEAD_COLUMNS.map((column) => (
                <TableHead
                  key={column.key}
                  align={column.align ?? "left"}
                  sortable={false}
                  showSortIcon={false}
                  className={cn(
                    "whitespace-nowrap sticky -top-[2px] z-10 bg-slate-100",
                  )}
                  style={{
                    minWidth: `${column.minWidth}px`,
                    width:
                      column.key === "aiSummary"
                        ? `${AI_SUMMARY_COLUMN_WIDTH}px`
                        : undefined,
                    maxWidth:
                      column.key === "aiSummary"
                        ? `${AI_SUMMARY_COLUMN_WIDTH}px`
                        : undefined,
                  }}
                >
                  {column.label}
                </TableHead>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell
                  colSpan={INTENT_LEAD_COLUMNS.length}
                  className="text-center text-slate-500"
                >
                  Loading intent leads...
                </TableCell>
              </TableRow>
            ) : null}
            {error ? (
              <TableRow>
                <TableCell
                  colSpan={INTENT_LEAD_COLUMNS.length}
                  className="text-center text-red-500"
                >
                  {error}
                </TableCell>
              </TableRow>
            ) : null}
            {shouldShowEmptyState ? (
              <TableRow>
                <TableCell
                  colSpan={INTENT_LEAD_COLUMNS.length}
                  className="text-center text-slate-500"
                >
                  No record found
                </TableCell>
              </TableRow>
            ) : null}
            {intentLeads.map((intentLead, rowIndex) => (
              <TableRow key={intentLead.rowKey}>
                {INTENT_LEAD_COLUMNS.map((column) => {
                  const isAdded = addedLeadIds.has(intentLead.rowKey);
                  const isAdding = addingLeadId === intentLead.rowKey;

                  return (
                    <TableCell
                      key={`${column.key}-${intentLead.rowKey}`}
                      className={cn(
                        column.key === "aiSummary"
                          ? "whitespace-nowrap align-middle"
                          : "whitespace-nowrap break-normal",
                      )}
                      style={{
                        minWidth: `${column.minWidth}px`,
                        width:
                          column.key === "aiSummary"
                            ? `${AI_SUMMARY_COLUMN_WIDTH}px`
                            : undefined,
                        maxWidth:
                          column.key === "aiSummary"
                            ? `${AI_SUMMARY_COLUMN_WIDTH}px`
                            : undefined,
                      }}
                    >
                      {column.key === "serialNumber" ? rowIndex + 1 : null}
                      {column.key === "action" ? (
                        <button
                          type="button"
                          onClick={actionHandlers[intentLead.rowKey]}
                          disabled={isAdding || isAdded}
                          className="primary-gradient-btn rounded border-0 font-semibold disabled:cursor-not-allowed"
                          style={{
                            padding: "6px 12px",
                            fontSize: "0.9rem",
                            opacity: isAdding || isAdded ? 0.65 : 1,
                          }}
                        >
                          {isAdding ? "Adding..." : isAdded ? "Added" : "Add Lead"}
                        </button>
                      ) : null}
                      {column.key !== "serialNumber" && column.key !== "action"
                        ? column.key === "aiSummary"
                          ? (
                            <span
                              className="block overflow-hidden whitespace-nowrap leading-5"
                              style={{
                                width: `${AI_SUMMARY_TEXT_WIDTH}px`,
                                maxWidth: `${AI_SUMMARY_TEXT_WIDTH}px`,
                                textOverflow: "ellipsis",
                              }}
                              title={intentLead[column.key] ?? "-"}
                            >
                              {intentLead[column.key] ?? "-"}
                            </span>
                          )
                          : (intentLead[column.key] ?? "-")
                        : null}
                    </TableCell>
                  );
                })}
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
      {leadPendingConfirmation ? (
        <div
          className="modal fade show d-block"
          role="dialog"
          aria-modal="true"
          aria-labelledby="confirm-add-intent-lead-title"
          tabIndex="-1"
          style={{ backgroundColor: "rgba(15, 23, 42, 0.45)" }}
        >
          <div className="modal-dialog modal-dialog-centered">
            <div className="modal-content border-0 shadow">
              <div className="modal-header">
                <h5 className="modal-title" id="confirm-add-intent-lead-title">
                  Add lead?
                </h5>
                <button
                  type="button"
                  className="btn-close"
                  aria-label="Close"
                  onClick={handleCloseConfirmation}
                  disabled={isConfirmingLead}
                />
              </div>
              <div className="modal-body">
                <p className="mb-0">
                  Are you sure you want to add{" "}
                  <strong>{leadPendingConfirmation.name ?? "this lead"}</strong>{" "}
                  to your leads?
                </p>
              </div>
              <div className="modal-footer">
                <button
                  type="button"
                  className="btn btn-outline-secondary"
                  onClick={handleCloseConfirmation}
                  disabled={isConfirmingLead}
                >
                  Cancel
                </button>
                <button
                  type="button"
                  className="primary-gradient-btn rounded border-0 font-semibold"
                  onClick={handleConfirmAddLead}
                  disabled={isConfirmingLead}
                  style={{
                    padding: "8px 14px",
                    opacity: isConfirmingLead ? 0.65 : 1,
                  }}
                >
                  {isConfirmingLead ? "Adding..." : "Confirm"}
                </button>
              </div>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default IntentLeadsBoard;
